import React from "react";
import { StyleSheet, View } from "react-native";

import Colors from "../../../Resources/Color";
import PulseSkeleton from "../../Components/PulseSkeleton";

const CARD_COUNT_BY_SCREEN_SIZE = {
  small: 3,
  default: 4,
};

function getCardCount(screenSize) {
  return screenSize === "small"
    ? CARD_COUNT_BY_SCREEN_SIZE.small
    : CARD_COUNT_BY_SCREEN_SIZE.default;
}

function getColumnCount(screenSize) {
  return screenSize === "small" ? 1 : 2;
}

function getGap(screenSize) {
  return screenSize === "small" ? 8 : 12;
}

function SkeletonCardShell({ children, style }) {
  return <View style={[styles.cardShell, style]}>{children}</View>;
}

function FoodSkeletonCard() {
  return (
    <SkeletonCardShell style={styles.rowCardShell}>
      <View style={styles.rowCardLeft}>
        <PulseSkeleton height={16} style={styles.foodTitleLine} width="62%" />
        <PulseSkeleton height={10} style={styles.foodSubtitleLine} width="34%" />
      </View>
      <View style={styles.rowCardRight}>
        <PulseSkeleton height={14} style={styles.kcalLine} width={50} />
        <PulseSkeleton height={11} style={styles.macroLine} width={112} />
      </View>
    </SkeletonCardShell>
  );
}

function EquivalentSkeletonCard() {
  return (
    <SkeletonCardShell style={styles.rowCardShell}>
      <View style={styles.rowCardLeft}>
        <PulseSkeleton height={16} style={styles.foodTitleLine} width="58%" />
        <View style={styles.previewList}>
          <PulseSkeleton height={11} style={styles.previewLine} width="88%" />
          <PulseSkeleton height={11} style={styles.previewLine} width="76%" />
          <PulseSkeleton height={11} style={styles.previewLine} width="64%" />
        </View>
      </View>
      <View style={[styles.rowCardRight, styles.fixedRightColumn]}>
        <PulseSkeleton height={14} style={styles.kcalLine} width={50} />
        <PulseSkeleton height={11} style={styles.macroLine} width={112} />
      </View>
    </SkeletonCardShell>
  );
}

function RecipeSkeletonCard() {
  return (
    <SkeletonCardShell>
      <View style={styles.recipeCardContent}>
        <View style={styles.recipeCardLeft}>
          <PulseSkeleton height={16} style={styles.foodTitleLine} width="56%" />
          <PulseSkeleton height={10} style={styles.recipeMetaLine} width="42%" />
          <View style={styles.previewList}>
            <PulseSkeleton height={11} style={styles.previewLine} width="86%" />
            <PulseSkeleton height={11} style={styles.previewLine} width="72%" />
            <PulseSkeleton height={11} style={styles.previewLine} width="60%" />
          </View>
        </View>
        <View style={styles.recipeCardRight}>
          <PulseSkeleton height={14} style={styles.kcalLine} width={50} />
          <PulseSkeleton height={11} style={styles.recipeMacroLine} width={118} />
        </View>
      </View>
    </SkeletonCardShell>
  );
}

function PlanSkeletonCard() {
  return (
    <SkeletonCardShell style={styles.planCardShell}>
      <View style={styles.planMain}>
        <View style={styles.rowCardLeft}>
          <PulseSkeleton height={16} style={styles.foodTitleLine} width="60%" />
        </View>
        <View style={styles.rowCardRight}>
          <PulseSkeleton height={14} style={styles.kcalLine} width={50} />
          <PulseSkeleton height={11} style={styles.macroLine} width={112} />
        </View>
      </View>
      <View style={styles.planFooter}>
        <PulseSkeleton height={10} style={styles.planUpdatedLine} width="36%" />
        <PulseSkeleton height={26} style={styles.planAssignPill} width={78} />
      </View>
    </SkeletonCardShell>
  );
}

function renderCard(variant, key) {
  if (variant === "equivalent") {
    return <EquivalentSkeletonCard key={key} />;
  }

  if (variant === "recipe") {
    return <RecipeSkeletonCard key={key} />;
  }

  if (variant === "plan") {
    return <PlanSkeletonCard key={key} />;
  }

  return <FoodSkeletonCard key={key} />;
}

export function NutritionLibrarySkeletonGrid({
  screenSize = "large",
  variant = "food",
}) {
  const gap = getGap(screenSize);
  const columnCount = getColumnCount(screenSize);
  const cardCount = getCardCount(screenSize);
  const cardWidth = `calc((100% - ${(columnCount - 1) * gap}px) / ${columnCount})`;

  return (
    <View style={[styles.grid, { gap }]}>
      {Array.from({ length: cardCount }, (_, index) => (
        <View key={`${variant}-skeleton-${index}`} style={{ width: cardWidth }}>
          {renderCard(variant, `${variant}-card-${index}`)}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  cardShell: {
    borderRadius: 12,
    backgroundColor: Colors.whiteColor,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    overflow: "hidden",
    width: "100%",
  },
  rowCardShell: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    paddingHorizontal: 18,
  },
  rowCardLeft: {
    flex: 1,
    minWidth: 0,
    marginRight: 16,
  },
  rowCardRight: {
    alignItems: "flex-end",
    gap: 4,
  },
  fixedRightColumn: {
    width: 120,
  },
  recipeCardContent: {
    flexDirection: "row",
    alignItems: "center",
    minWidth: 0,
    paddingVertical: 16,
    paddingLeft: 16,
    paddingRight: 18,
  },
  recipeCardLeft: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    marginRight: 12,
  },
  recipeCardRight: {
    flexGrow: 1,
    alignItems: "flex-end",
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    gap: 4,
  },
  planCardShell: {
    borderRadius: 12,
  },
  planMain: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    paddingHorizontal: 18,
  },
  planFooter: {
    alignItems: "center",
    borderTopWidth: 0.5,
    borderTopColor: Colors.borderColor,
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 12,
  },
  foodTitleLine: {
    borderRadius: 5,
  },
  foodSubtitleLine: {
    borderRadius: 5,
    marginTop: 7,
  },
  recipeMetaLine: {
    borderRadius: 5,
    marginTop: 8,
  },
  previewList: {
    marginTop: 6,
    gap: 4,
    height: 70,
    justifyContent: "flex-start",
  },
  previewLine: {
    borderRadius: 5,
  },
  kcalLine: {
    borderRadius: 5,
  },
  macroLine: {
    borderRadius: 5,
  },
  recipeMacroLine: {
    borderRadius: 5,
  },
  planUpdatedLine: {
    borderRadius: 5,
    flexShrink: 1,
  },
  planAssignPill: {
    borderRadius: 8,
    marginLeft: "auto",
  },
});
